import type { AxiosRequestConfig, AxiosResponse } from 'axios';

import { axiosDefault } from 'api/axiosDefault';
import client from 'api/client';
import type {
    AgreementDocumentInterface,
    ApplicationRetainer,
    ApplicationRetainersList,
    CreateApplicationRetainerPayload,
    UpdateApplicationRetainerSigningPayload,
} from 'types';

const retainersPath = (applicationId: number | string) => `/application/${applicationId}/retainers`;

const retainerPath = (applicationId: number | string, agreementId: number | string) =>
    `${retainersPath(applicationId)}/${agreementId}`;

export function list(
    applicationId: number | string
): Promise<AxiosResponse<ApplicationRetainersList>> {
    return client.get<ApplicationRetainersList>(retainersPath(applicationId));
}

export function create(
    applicationId: number | string,
    payload: CreateApplicationRetainerPayload
): Promise<AxiosResponse<ApplicationRetainer>> {
    return client.post<ApplicationRetainer>(retainersPath(applicationId), payload);
}

export function get(
    applicationId: number | string,
    agreementId: number | string
): Promise<AxiosResponse<ApplicationRetainer>> {
    return client.get<ApplicationRetainer>(retainerPath(applicationId, agreementId));
}

export function updateSigningBlocked(
    applicationId: number | string,
    agreementId: number | string,
    payload: UpdateApplicationRetainerSigningPayload
): Promise<AxiosResponse<ApplicationRetainer>> {
    return client.patch<ApplicationRetainer>(retainerPath(applicationId, agreementId), payload);
}

export function getPdf(
    applicationId: number | string,
    agreementId: number | string
): Promise<AxiosResponse<Blob>> {
    return client.get<Blob>(`${retainerPath(applicationId, agreementId)}/pdf`, {
        responseType: 'blob',
    });
}

export function sign(
    applicationId: number | string,
    agreementId: number | string,
    payload: FormData,
    customConfig?: AxiosRequestConfig<FormData>
): Promise<AxiosResponse<{ msg: string }>> {
    return client.post<{ msg: string }>(
        `${retainerPath(applicationId, agreementId)}/sign`,
        payload,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
            ...customConfig,
        }
    );
}

/** Emails the client a link to `/visa-process/:applicationId/agreement/:agreementId`. */
export function send(
    applicationId: number | string,
    agreementId: number | string
): Promise<AxiosResponse<{ msg?: string }>> {
    return client.post<{ msg?: string }>(`${retainerPath(applicationId, agreementId)}/send`);
}

export function remove(
    applicationId: number | string,
    agreementId: number | string
): Promise<AxiosResponse<unknown>> {
    return client.delete<unknown>(retainerPath(applicationId, agreementId));
}

/**
 * Unsigned retainers stream from `.../pdf` (409 once signed); signed ones are
 * served from the stored `pdf_url`.
 */
export async function getDocument(
    applicationId: number | string,
    agreementId: number | string
): Promise<AgreementDocumentInterface> {
    const { document } = await getRetainerWithDocument(applicationId, agreementId);
    return document;
}

/** Document plus the full retainer row (type, sent/signed state) for the client page. */
export async function getRetainerWithDocument(
    applicationId: number | string,
    agreementId: number | string
): Promise<{ retainer: ApplicationRetainer; document: AgreementDocumentInterface }> {
    const { data: retainer } = await get(applicationId, agreementId);
    const { data: file } =
        retainer.signed && retainer.pdf_url
            ? await axiosDefault.get<Blob>(retainer.pdf_url, { responseType: 'blob' })
            : await getPdf(applicationId, agreementId);

    return {
        retainer,
        document: {
            file,
            summary: {
                application_id: retainer.application_id,
                agreement_id: retainer.agreement_id,
                created_from: retainer.created_from,
                signed: retainer.signed,
                signing_blocked: retainer.signing_blocked,
                signed_at: retainer.signed_at,
                pdf_url: retainer.pdf_url,
                signing: retainer.signing,
            },
        },
    };
}
